import { useEffect, useRef, useState } from "react";
import { cleanTag, MAX_TAGS, PRODUCT_TAGS, TAG_MAX_LENGTH } from "../../menu.js";
import { GALLERY_PRO_ONLY } from "../../plans.js";
import { galleryAvailable, initialPhotos, makeMain, MAX_PHOTOS, movePhoto, photoFileProblem } from "../../gallery.js";
import { cleanLine, cleanParagraph, textProblem } from "../../businessValidation.js";
import Icon from "./Icon";
import { notifyError } from "./useBusiness";

// Alta y edición de un producto. Las etiquetas y las fotos extra son del plan Pro (`pro`);
// en Gratis se ven bloqueadas y el producto lleva una sola foto.
const ProductForm = ({ product, categories, defaultCategory, onSubmit, onCancel, pro }) => {

    const [name, setName] = useState(product?.name || "");
    const [price, setPrice] = useState(product?.price || "");
    const [category, setCategory] = useState(product?.category || defaultCategory || "");
    const [description, setDescription] = useState(product?.description || "");
    // Fotos en orden, la principal primero: { key, url, path } las guardadas y { key, url, file } las nuevas.
    const [photos, setPhotos] = useState(() => initialPhotos(product));
    const [photosChanged, setPhotosChanged] = useState(false);
    const gallery = galleryAvailable({ subscribed: pro }, GALLERY_PRO_ONLY);
    const maxPhotos = gallery ? MAX_PHOTOS : 1;
    const created = useRef([]);
    useEffect(() => () => created.current.forEach((url) => URL.revokeObjectURL(url)), []);

    const changePhotos = (next) => {
        setPhotos(next);
        setPhotosChanged(true);
    }

    const addPhotos = (files) => {
        const chosen = Array.from(files || []);
        if (!chosen.length) return;
        const problem = chosen.map(photoFileProblem).find(Boolean);
        if (problem) {
            notifyError(problem);
            return;
        }
        // En Gratis elegir otra foto reemplaza la que había.
        const base = gallery ? photos : [];
        const room = maxPhotos - base.length;
        if (room <= 0) {
            notifyError(`Cada producto puede tener hasta ${MAX_PHOTOS} fotos.`);
            return;
        }
        if (chosen.length > room) notifyError(`Cada producto puede tener hasta ${MAX_PHOTOS} fotos: agregamos las primeras ${room}.`);
        const added = chosen.slice(0, room).map((file) => {
            const url = URL.createObjectURL(file);
            created.current.push(url);
            return { key: url, url, file };
        });
        changePhotos([...base, ...added]);
    }
    const [tags, setTags] = useState(product?.etiquetas || []);
    const [customTag, setCustomTag] = useState("");
    const [saving, setSaving] = useState(false);

    const full = tags.length >= MAX_TAGS;
    const custom = tags.filter((t) => !PRODUCT_TAGS.includes(t));
    const toggleTag = (tag) => setTags((list) => list.includes(tag) ? list.filter((t) => t !== tag) : list.length < MAX_TAGS ? [...list, tag] : list);

    // Etiqueta escrita por el negocio. Si coincide con una sugerida (sin importar mayúsculas), usa esa.
    const addCustomTag = () => {
        const clean = cleanTag(customTag);
        if (!clean) return;
        const tag = PRODUCT_TAGS.find((t) => t.toLowerCase() === clean.toLowerCase()) || clean;
        if (!tags.some((t) => t.toLowerCase() === tag.toLowerCase())) {
            if (full) {
                notifyError(`Cada producto puede tener hasta ${MAX_TAGS} etiquetas.`);
                return;
            }
            setTags((list) => [...list, tag]);
        }
        setCustomTag("");
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        const fields = { name: cleanLine(name), price: typeof price === "string" ? price.trim() : price, category, description: cleanParagraph(description) };
        const problem = textProblem("El nombre", fields.name, 80) || textProblem("La descripción", fields.description, 400)
            || (/^[0-9]{1,9}([.,][0-9]{1,2})?$/.test(String(price).trim()) ? null : "Revisá el precio: tiene que ser un número, con hasta 2 decimales.");
        if (problem) {
            notifyError(problem);
            return;
        }
        setName(fields.name);
        setDescription(fields.description);
        setSaving(true);
        if (pro) fields.etiquetas = tags.map(cleanTag).filter(Boolean).slice(0, MAX_TAGS);
        // Al crear hace falta una foto; al editar, las fotos solo se mandan si cambiaron.
        if (!product && !photos.length) {
            notifyError("Elegí al menos una foto del producto.");
            setSaving(false);
            return;
        }
        const ok = await onSubmit(fields, product && !photosChanged ? undefined : photos.slice(0, maxPhotos));
        if (!ok) setSaving(false);
    }

    if (!categories.length) {
        return (
            <div className="lp-panel-form">
                <p className="lp-panel-muted">Primero creá una categoría (por ejemplo "Pizzas" o "Bebidas") para ordenar tus productos en el menú.</p>
                <div className="lp-panel-actions">
                    <button className="lp-btn lp-btn-secondary" type="button" onClick={onCancel}>Entendido</button>
                </div>
            </div>
        )
    }

  return (
    <form className="lp-panel-form" onSubmit={handleSubmit}>
        <div className="lp-field">
            <label htmlFor="pf-name">Nombre</label>
            <input id="pf-name" type="text" required maxLength={80} value={name} onChange={(e) => setName(e.target.value)}/>
        </div>

        <div className="lp-panel-grid-2">
            <div className="lp-field">
                <label htmlFor="pf-price">Precio</label>
                <input id="pf-price" type="number" min="0" step="any" inputMode="decimal" required value={price} onChange={(e) => setPrice(e.target.value)}/>
            </div>
            <div className="lp-field">
                <label htmlFor="pf-category">Categoría</label>
                <select id="pf-category" required value={category} onChange={(e) => setCategory(e.target.value)}>
                    <option value="" disabled>Elegí una</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>
        </div>

        <div className="lp-field">
            <label htmlFor="pf-desc">Descripción</label>
            <textarea id="pf-desc" rows="3" required maxLength={400} value={description} onChange={(e) => setDescription(e.target.value)}></textarea>
        </div>

        <div className="lp-field">
            <span className="lp-panel-label" id="pf-tags">Etiquetas {!pro && <span className="lp-panel-badge">Pro</span>}</span>
            <div className="lp-panel-tag-picks" role="group" aria-labelledby="pf-tags">
                {PRODUCT_TAGS.map((t) => (
                    <button key={t} type="button" disabled={!pro || (full && !tags.includes(t))} aria-pressed={tags.includes(t)} className={tags.includes(t) ? "lp-panel-chip lp-panel-chip-on" : "lp-panel-chip"} onClick={() => toggleTag(t)}>{t}</button>
                ))}
                {custom.map((t) => (
                    <button key={t} type="button" disabled={!pro} className="lp-panel-chip lp-panel-chip-on" aria-label={`Quitar la etiqueta ${t}`} onClick={() => toggleTag(t)}>
                        {t} <Icon name="close" size={14} strokeWidth={2.6}></Icon>
                    </button>
                ))}
            </div>
            {pro &&
                <div className="lp-panel-tag-custom">
                    <label htmlFor="pf-tag" className="lp-panel-sr">Etiqueta personalizada</label>
                    <input id="pf-tag" type="text" maxLength={TAG_MAX_LENGTH} placeholder="Otra, ej.: Casero" value={customTag} disabled={full} onChange={(e) => setCustomTag(e.target.value)} onKeyDown={(e) => {
                        // Enter agrega la etiqueta en vez de guardar el producto.
                        if (e.key === "Enter") {
                            e.preventDefault();
                            addCustomTag();
                        }
                    }}/>
                    <button type="button" className="lp-panel-chip lp-panel-chip-add" disabled={full || !cleanTag(customTag)} onClick={addCustomTag}><Icon name="plus" size={16} strokeWidth={2.6}></Icon> Agregar</button>
                </div>
            }
            <span className="lp-field-hint">{pro ? `Hasta ${MAX_TAGS} por producto. Se muestran arriba del nombre en tu menú.` : "Con Pro podés destacar productos como Nuevo, Promo o Sin TACC, o con tus propias etiquetas."}</span>
        </div>

        <div className="lp-field">
            <span className="lp-panel-label" id="pf-photos">{gallery ? "Fotos" : "Foto"} {!gallery && <span className="lp-panel-badge">Pro: hasta {MAX_PHOTOS}</span>}</span>
            <ul className="lp-panel-photos" aria-labelledby="pf-photos">
                {photos.map((p, i) => (
                    <li key={p.key} className={i === 0 ? "lp-panel-photo lp-panel-photo-main" : "lp-panel-photo"}>
                        <img src={p.url} alt={i === 0 ? "Foto principal" : `Foto ${i + 1}`}/>
                        {i === 0 && photos.length > 1 && <span className="lp-panel-photo-tag">Principal</span>}
                        <span className="lp-panel-photo-tools">
                            {gallery && i > 0 &&
                                <button type="button" className="lp-panel-photo-btn lp-panel-photo-left" aria-label={`Mover la foto ${i + 1} a la izquierda`} title="Mover a la izquierda" onClick={() => changePhotos(movePhoto(photos, i, i - 1))}><Icon name="arrow" size={16} strokeWidth={2.4}></Icon></button>
                            }
                            {gallery && i > 0 &&
                                <button type="button" className="lp-panel-photo-btn lp-panel-photo-star" aria-label={`Usar la foto ${i + 1} como principal`} title="Usar como principal" onClick={() => changePhotos(makeMain(photos, i))}><Icon name="star" size={16} strokeWidth={2.2}></Icon></button>
                            }
                            <button type="button" className="lp-panel-photo-btn" aria-label={`Quitar la foto ${i + 1}`} title="Quitar" onClick={() => changePhotos(photos.filter((_, j) => j !== i))}><Icon name="close" size={16} strokeWidth={2.4}></Icon></button>
                        </span>
                    </li>
                ))}
                {(photos.length < maxPhotos || !gallery) &&
                    <li className="lp-panel-photo lp-panel-photo-add">
                        <label htmlFor="pf-image">
                            <Icon name={photos.length ? "plus" : "camera"} size={22}></Icon>
                            <span>{!gallery && photos.length ? "Cambiar" : photos.length ? "Agregar" : "Elegir foto"}</span>
                        </label>
                        <input id="pf-image" className="lp-panel-file" type="file" accept="image/*" multiple={gallery} onChange={(e) => {
                            addPhotos(e.target.files);
                            e.target.value = "";
                        }}/>
                    </li>
                }
            </ul>
            <span className="lp-field-hint">{gallery
                ? `Hasta ${MAX_PHOTOS} fotos. La primera es la principal: es la que se ve en la lista del menú. Las demás se ven al tocar el producto.`
                : `Con Pro podés sumar hasta ${MAX_PHOTOS} fotos por producto y tus clientes las pasan deslizando.`}</span>
        </div>

        <div className="lp-panel-actions">
            <button className="lp-btn lp-btn-primary" type="submit" disabled={saving}>{saving ? "Guardando..." : product ? "Guardar cambios" : "Agregar producto"}</button>
            <button className="lp-btn lp-btn-secondary" type="button" onClick={onCancel}>Cancelar</button>
        </div>
    </form>
  )
}

export default ProductForm
